import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Bell, Heart, MessageSquare, TrendingDown, HandCoins } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuthedApi } from "../lib/api";
import { useRealtime } from "../lib/realtime";
import { useFavorites } from "../lib/favorites";

const ICONS = { message: MessageSquare, price_drop: TrendingDown, offer: HandCoins, offer_status: HandCoins };
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};
const iconBtn = "relative w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37]/60 flex items-center justify-center text-gray-200 hover:text-[#E5C158] transition-colors";
const Badge = ({ n, testid, dot }) => n > 0 && (
  <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${dot ? "bg-red-500 text-white" : "text-black"}`} style={dot ? {} : { background: "var(--gold-grad)" }} data-testid={testid}>{n > 99 ? "99+" : n}</span>
);

export function HeartLink({ testid = "header-saved-btn" }) {
  const { ids } = useFavorites();
  const { isSignedIn } = useAuth();
  return (
    <Link to={isSignedIn ? "/dashboard?tab=saved" : "/sign-in"} className={iconBtn} aria-label="Saved cars" data-testid={testid}>
      <Heart className={`w-4 h-4 transition-transform ${ids.size ? "fill-[#D4AF37] text-[#D4AF37] scale-110" : ""}`} />
      <Badge n={ids.size} testid={`${testid}-count`} />
    </Link>
  );
}

export function NotificationBell({ testid = "header-bell-btn" }) {
  const { isSignedIn } = useAuth();
  const api = useAuthedApi();
  const navigate = useNavigate();
  const { counts, refresh } = useRealtime();
  const [items, setItems] = useState(null);

  const onOpen = async (o) => {
    if (!o) return;
    setItems(null);
    const list = await api("get", "/notifications").catch(() => []);
    setItems(list);
    if (list.some((n) => !n.read)) { await api("post", "/notifications/read-all").catch(() => {}); refresh(); }
  };

  if (!isSignedIn) return <Link to="/sign-in" className={iconBtn} aria-label="Notifications" data-testid={testid}><Bell className="w-4 h-4" /></Link>;
  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className={iconBtn} aria-label="Notifications" data-testid={testid}>
          <Bell className="w-4 h-4" /><Badge n={counts.notifications} dot testid={`${testid}-count`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0 bg-[#111319] border-[#D4AF37]/30 text-white" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <p className="font-semibold text-sm">Notifications</p>
          <Link to="/chat" className="text-xs text-[#E5C158]" data-testid="notifications-open-chats">Open chats</Link>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!items && <p className="p-4 text-xs text-gray-500">Loading...</p>}
          {items && !items.length && <p className="p-6 text-center text-xs text-gray-400" data-testid="notifications-empty">No notifications yet. Messages, offers and price drops will appear here.</p>}
          {items?.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <button key={n.id} onClick={() => n.link && navigate(n.link)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/[0.03] border-b border-white/5 ${n.read ? "" : "bg-[#D4AF37]/5"}`} data-testid={`notification-${n.id}`}>
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#D4AF37]/15 flex items-center justify-center"><Icon className="w-4 h-4 text-[#D4AF37]" /></span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold leading-snug">{n.title}</span>
                  {n.body && <span className="block text-[11px] text-gray-400 truncate">{n.body}</span>}
                  <span className="block text-[10px] text-gray-500 mt-0.5">{ago(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
